import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  formatPersistentEditMetadata,
  formatPersistentEditValue,
} from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import type { IAiSpreadsheetCellOverride } from "@/payroll/ai-script/types";
import { InlineConfirmation } from "./inline-confirmation";
import { EditCard } from "./shared";

interface HistoryEntryProps {
  version: IAiSpreadsheetCellOverride;
  canRestore: boolean;
  isRestoring: boolean;
  pending: boolean;
  onRestore: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export const HistoryEntry = ({
  version,
  canRestore,
  isRestoring,
  pending,
  onRestore,
  onConfirm,
  onCancel,
}: HistoryEntryProps) => {
  const metadata = formatPersistentEditMetadata(version);

  return (
    <EditCard data-edit-history-entry>
      <Stack
        direction="row"
        gap="sm"
        align="center"
        justify="between"
        fullWidth
      >
        <Stack gap="none" className="min-w-0 flex-1">
          <Typography variant="bodyW1" className="truncate font-semibold">
            {formatPersistentEditValue(version.value?.computed_value)}
          </Typography>
          <Typography
            variant="captionW1"
            textColor="tertiary"
            className="truncate"
          >
            {metadata}
          </Typography>
        </Stack>
        {canRestore && !pending ? (
          <Button
            variant="tertiary"
            size="xs"
            className="shrink-0"
            loading={isRestoring}
            disabled={isRestoring}
            onClick={onRestore}
          >
            Restore
          </Button>
        ) : null}
      </Stack>
      {pending ? (
        <InlineConfirmation
          message="Go back to this version? Any edits made to this cell after it are discarded."
          confirmLabel="Use this version"
          isPending={isRestoring}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      ) : null}
    </EditCard>
  );
};
